'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import dynamic from 'next/dynamic';

import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useUrl } from '@packages/shared/src/hooks/useUrl/useUrl';
import { DialogHeader } from '../../../Dialog/DialogHeader/DialogHeader.client';
import { useNavigationPopoverClose } from './useNavigationPopoverClose';
import { ResponsiveNavigationMainItem } from './ResponsiveNavigationMainItem.client';
import type { NavigationItem, RawNavigationItem } from './navigationTypes';
import { useCleanedNavData } from './useCleanedNavData';
import type { UseTreeNavigationPopupOptions } from './Flyout/useTreeNavigationPopup';
import { useNavigationTracking } from './useNavigationTracking';
import { useTreeNavigationPopup } from './Flyout/useTreeNavigationPopup';
import { HamburgerButton } from './Internals/HamburgerButton.client';
import { useToggleTransition } from './useToggleTransition';
import { SecondaryNavigationContainer } from './Internals/SecondaryNavigationContainer';
import { PrimaryNavigationContainer } from './Internals/PrimaryNavigationContainer.client';
import { ResponsiveNavigationLayout } from './Internals/ResponsiveNavigationLayout';
import { MetaNavigationLinks } from '../../MetaNavigation/MobileLinks/MetaNavigationLinks.client';

const ResponsiveFlyout = dynamic(() => import('./Flyout/ResponsiveFlyout.client'), {
  ssr: false,
});

export type ResponsiveNavigationProps = {
  /** The nav tree, as a polyforest (in the graph theory sense) of navigation items */
  items: RawNavigationItem[];
} & Pick<UseTreeNavigationPopupOptions, 'mode' | 'onFirstInteraction'>;

/**
 * Navigation component, which displays a horizontal navigation bar on desktop and a hamburger menu on mobile. Shows {@link ResponsiveFlyout} on user interaction, to enable drilling into the nav tree.
 */
export const ResponsiveNavigation: FC<ResponsiveNavigationProps> = ({
  items,
  mode = 'click',
  onFirstInteraction,
}) => {
  const {
    shouldBeMounted: hamburgerShouldBeMounted,
    classNames: hamburgerClassNames,
    toggle: toggleHamburgerMenu,
    hide: hideHamburgerMenu,
    state,
  } = useToggleTransition(300);

  const {
    navigationDirection,
    showPopup,
    isSelected,
    isRoot,
    selectMainItem,
    selectPopupItem,
    backToParent,
    closePopup,
  } = useTreeNavigationPopup({ onFirstInteraction });

  const { isDesktop } = useDeviceType();
  const allowHoverInteractions = isDesktop && mode === 'hover';
  const url = useUrl();
  const primaryNavigationRef = useRef<HTMLUListElement | null>(null);
  const secondaryNavigationRef = useRef<HTMLDivElement | null>(null);
  const mainNavigationRef = useRef<HTMLElement | null>(null);
  const [shouldFocusCloseButton, setShouldFocusCloseButton] = useState(false);
  const closeFlyoutAndHamburgerMenu = useCallback(() => {
    closePopup();
    setShouldFocusCloseButton(false);
    hideHamburgerMenu();
  }, [closePopup, hideHamburgerMenu]);

  const { handlePrimaryMouseLeave, handleSecondaryMouseLeave, onBlurFlyOut } =
    useNavigationPopoverClose({
      closeFlyoutAndHamburgerMenu,
      allowHoverInteractions,
      url,
      primaryNavigationRef,
      secondaryNavigationRef,
      mainNavigationRef,
    });

  const { trackNavigationTo } = useNavigationTracking();

  useEffect(() => {
    // INSPECT-978 prevent body scroll when the menu is open
    // this is only needed for the logged out myaccount navigation because the content is too short and
    // overscroll-contain is only working for content that is scrollable
    // the logged in myaccount navigation is scrollable and so body locking is not needed
    if (!isDesktop && state.startsWith('enter')) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [state, isDesktop]);

  const handleNavigate = (item: NavigationItem) => {
    trackNavigationTo(item);
    // running both produces flicker when the mobile menu closes on Navigation.
    if (isDesktop) {
      setShouldFocusCloseButton(true);
      closePopup();
    } else hideHamburgerMenu();
  };

  const { cleanItems, flatItems } = useCleanedNavData(items);

  const selectedItem = flatItems.find(isSelected);

  return (
    <ResponsiveNavigationLayout
      hamburgerClassNames={hamburgerClassNames}
      hamburgerShouldBeMounted={hamburgerShouldBeMounted}
      button={<HamburgerButton onClick={toggleHamburgerMenu} />}
      isLevel2Visible={showPopup}
      ref={mainNavigationRef}
    >
      <div className="[grid-area:header] lg:hidden">
        <DialogHeader
          onBack={selectedItem ? backToParent : hideHamburgerMenu}
          onClose={hideHamburgerMenu}
        >
          {selectedItem?.displayName ?? ''}
        </DialogHeader>
      </div>

      <div className="overflow-hidden [grid-area:content] max-lg:overflow-y-auto max-lg:overscroll-contain">
        {!showPopup && <MetaNavigationLinks />}

        <PrimaryNavigationContainer
          ref={primaryNavigationRef}
          primaryNavigationVisible={!selectedItem}
          onMouseLeave={handlePrimaryMouseLeave}
        >
          {cleanItems.map((item) => (
            <ResponsiveNavigationMainItem
              key={item.uri}
              href={item.uri}
              imageUrl={item.image}
              allowNavigation={allowHoverInteractions || item.children.length === 0}
              isOpen={showPopup && isRoot(item)}
              // trailing slash is stripped for the comparison, because `url` has no trailing slash by default
              isActive={item.uri !== undefined && url.startsWith(item.uri.replace(/\/$/, ''))}
              onHover={() => {
                if (allowHoverInteractions) {
                  selectMainItem(item, true);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  event.stopPropagation();
                  selectMainItem(item);
                  setShouldFocusCloseButton(true);
                }
              }}
              onClick={() => {
                selectMainItem(item);
                setShouldFocusCloseButton(true);
              }}
              onNavigate={() => handleNavigate(item)}
            >
              {item.displayName}
            </ResponsiveNavigationMainItem>
          ))}
        </PrimaryNavigationContainer>
      </div>

      <SecondaryNavigationContainer
        onBlur={onBlurFlyOut}
        ref={secondaryNavigationRef}
        isVisible={showPopup}
        onMouseLeave={handleSecondaryMouseLeave}
      >
        <ResponsiveFlyout
          shouldFocusCloseButton={shouldFocusCloseButton}
          item={selectedItem}
          direction={navigationDirection}
          onBack={!selectedItem || isRoot(selectedItem) ? undefined : backToParent}
          onClose={closePopup}
          onItemClick={selectPopupItem}
          onNavigate={handleNavigate}
        />
      </SecondaryNavigationContainer>
    </ResponsiveNavigationLayout>
  );
};
